import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { toast } from 'sonner';
import { X, Save, Trash2, Languages, Loader2 } from 'lucide-react';

const OfferTranslationModal = ({ offer, isOpen, onClose, onSave }) => {
  const [translations, setTranslations] = useState({
    title: { en: '', es: '', fr: '' },
    description: { en: '', es: '', fr: '' }
  });
  const [loading, setLoading] = useState(false);
  const [translatingField, setTranslatingField] = useState(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    if (isOpen && offer) {
      loadTranslations();
    }
  }, [isOpen, offer]);

  const detectSourceLanguage = (text) => {
    if (!text) return null;
    const frenchWords = ['le', 'la', 'les', 'de', 'du', 'des', 'un', 'une', 'et', 'à', 'pour'];
    const spanishWords = ['el', 'la', 'los', 'las', 'de', 'del', 'un', 'una', 'y', 'para', 'con'];
    const englishWords = ['the', 'a', 'an', 'and', 'or', 'for', 'with', 'on', 'in', 'at'];
    
    const lowerText = text.toLowerCase();
    const frenchCount = frenchWords.filter(w => lowerText.includes(` ${w} `)).length;
    const spanishCount = spanishWords.filter(w => lowerText.includes(` ${w} `)).length;
    const englishCount = englishWords.filter(w => lowerText.includes(` ${w} `)).length;
    
    if (frenchCount > spanishCount && frenchCount > englishCount) return 'fr';
    if (spanishCount > frenchCount && spanishCount > englishCount) return 'es';
    if (englishCount > frenchCount && englishCount > spanishCount) return 'en';
    return 'fr';
  };

  const loadTranslations = async () => {
    const token = localStorage.getItem('admin_token');
    try {
      const response = await axios.get(
        `${API_URL}/api/admin/offer-translations/${offer.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const trans = response.data.translations || {};
      const newTranslations = {
        title: trans.title || { en: '', es: '', fr: '' },
        description: trans.description || { en: '', es: '', fr: '' }
      };
      
      const sourceTitle = offer.title || '';
      const sourceDesc = typeof offer.description === 'object' 
        ? (offer.description.fr || offer.description.en || offer.description.es || '')
        : (offer.description || '');
      
      const detectedLang = detectSourceLanguage(sourceTitle + ' ' + sourceDesc);
      
      if (detectedLang && !newTranslations.title[detectedLang]) {
        newTranslations.title[detectedLang] = sourceTitle;
      }
      if (detectedLang && !newTranslations.description[detectedLang]) {
        newTranslations.description[detectedLang] = sourceDesc;
      }
      
      setTranslations(newTranslations);
    } catch (error) {
      console.error('Failed to load translations:', error);
    }
  };

  const handleSave = async () => {
    const token = localStorage.getItem('admin_token');
    setLoading(true);
    
    try {
      await axios.post(
        `${API_URL}/api/admin/offer-translations/${offer.id}`,
        translations,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Translations saved successfully');
      onSave();
      onClose();
    } catch (error) {
      toast.error('Failed to save translations');
      console.error('Save error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete all custom translations for this offer?')) {
      return;
    }

    const token = localStorage.getItem('admin_token');
    setLoading(true);
    
    try {
      await axios.delete(
        `${API_URL}/api/admin/offer-translations/${offer.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Translations deleted');
      setTranslations({
        title: { en: '', es: '', fr: '' },
        description: { en: '', es: '', fr: '' }
      });
      onSave();
    } catch (error) {
      if (error.response?.status === 404) {
        toast.info('No translations to delete');
      } else {
        toast.error('Failed to delete translations');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleTranslateField = async (field, targetLang) => {
    const token = localStorage.getItem('admin_token');
    const fieldKey = `${field}_${targetLang}`;
    setTranslatingField(fieldKey);
    
    try {
      const sourceTitle = offer.title || '';
      const sourceDesc = typeof offer.description === 'object' 
        ? (offer.description.fr || offer.description.en || offer.description.es || '')
        : (offer.description || '');
      
      const sourceText = field === 'title' ? sourceTitle : sourceDesc;
      
      if (!sourceText) {
        toast.error(`No source ${field} to translate`);
        setTranslatingField(null);
        return;
      }
      
      // Fire translation request (backend processes in background and saves directly)
      await axios.post(
        `${API_URL}/api/admin/offer-translations/${offer.id}/translate-field`,
        { field, target_lang: targetLang, source_text: sourceText },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.info('Translation in progress...');
      
      // Poll offer-translations directly every 3s to detect when the field gets filled
      const currentValue = translations[field]?.[targetLang] || '';
      
      const pollInterval = setInterval(async () => {
        try {
          const res = await axios.get(
            `${API_URL}/api/admin/offer-translations/${offer.id}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          const trans = res.data.translations || {};
          const newValue = trans[field]?.[targetLang] || '';
          
          // Translation arrived: value changed from what it was before
          if (newValue && newValue !== currentValue) {
            clearInterval(pollInterval);
            setTranslations(prev => ({
              ...prev,
              title: { ...prev.title, ...(trans.title || {}) },
              description: { ...prev.description, ...(trans.description || {}) }
            }));
            toast.success(`${field} translated to ${targetLang.toUpperCase()}`);
            setTranslatingField(null);
          }
        } catch (e) {
          // Keep polling, don't stop on network blips
        }
      }, 3000);
      
      // Safety timeout after 6 minutes
      setTimeout(() => {
        clearInterval(pollInterval);
        setTranslatingField(prev => prev === fieldKey ? null : prev);
      }, 360000);
      
    } catch (error) {
      const detail = error.response?.data?.detail || 'Failed to start translation';
      toast.error(`Translation error: ${detail}`);
      setTranslatingField(null);
    }
  };

  const updateTranslation = (field, lang, value) => {
    setTranslations(prev => ({
      ...prev,
      [field]: {
        ...prev[field],
        [lang]: value
      }
    }));
  };

  if (!offer) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-[#1A2332] border-[#2D3748]">
        <DialogHeader>
          <DialogTitle className="text-white text-xl">
            Edit Translations: {offer.title}
          </DialogTitle>
          <DialogDescription className="text-gray-400">
            Customize titles and descriptions for each language. Leave empty to use WowBooking defaults.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 mt-4">
          <Tabs defaultValue="en" className="w-full">
            <TabsList className="bg-[#0F1729] border border-[#2D3748] w-full justify-start">
              <TabsTrigger 
                value="en" 
                className="data-[state=active]:bg-[#2D3748] data-[state=active]:text-white text-gray-400"
              >
                🇬🇧 English
              </TabsTrigger>
              <TabsTrigger 
                value="es" 
                className="data-[state=active]:bg-[#2D3748] data-[state=active]:text-white text-gray-400"
              >
                🇪🇸 Español
              </TabsTrigger>
              <TabsTrigger 
                value="fr" 
                className="data-[state=active]:bg-[#2D3748] data-[state=active]:text-white text-gray-400"
              >
                🇫🇷 Français
              </TabsTrigger>
            </TabsList>

            {['en', 'es', 'fr'].map(lang => (
              <TabsContent key={lang} value={lang} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-gray-300">Title</Label>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleTranslateField('title', lang)}
                      disabled={translatingField === `title_${lang}`}
                      className="border-purple-500 text-purple-400 hover:bg-purple-500/10 h-8"
                    >
                      {translatingField === `title_${lang}` ? (
                        <>
                          <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                          Translating...
                        </>
                      ) : (
                        <>
                          <Languages className="w-3 h-3 mr-1" />
                          Translate
                        </>
                      )}
                    </Button>
                  </div>
                  <Input
                    value={translations.title[lang]}
                    onChange={(e) => updateTranslation('title', lang, e.target.value)}
                    placeholder={`Enter ${lang.toUpperCase()} title or leave empty for default`}
                    className="bg-[#0F1729] border-[#2D3748] text-white placeholder:text-gray-500"
                  />
                  <p className="text-xs text-gray-500">
                    Default: {offer.title}
                  </p>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-gray-300">Description</Label>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleTranslateField('description', lang)}
                      disabled={translatingField === `description_${lang}`}
                      className="border-purple-500 text-purple-400 hover:bg-purple-500/10 h-8"
                    >
                      {translatingField === `description_${lang}` ? (
                        <>
                          <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                          Translating...
                        </>
                      ) : (
                        <>
                          <Languages className="w-3 h-3 mr-1" />
                          Translate
                        </>
                      )}
                    </Button>
                  </div>
                  <textarea
                    value={translations.description[lang]}
                    onChange={(e) => updateTranslation('description', lang, e.target.value)}
                    placeholder={`Enter ${lang.toUpperCase()} description or leave empty for default`}
                    rows={6}
                    className="w-full px-3 py-2 bg-[#0F1729] border border-[#2D3748] rounded-md text-white placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="text-xs text-gray-500">
                    Default: {offer.description && typeof offer.description === 'object' 
                      ? offer.description[lang] || offer.description.fr || 'N/A'
                      : offer.description || 'N/A'}
                  </p>
                </div>
              </TabsContent>
            ))}
          </Tabs>

          <div className="flex justify-between items-center pt-4 border-t border-[#2D3748]">
            <Button
              variant="outline"
              onClick={handleDelete}
              disabled={loading}
              className="border-red-500 text-red-500 hover:bg-red-500/10"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete All
            </Button>
            
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={onClose}
                disabled={loading}
                className="border-[#2D3748] text-gray-300 hover:bg-[#2D3748]"
              >
                <X className="w-4 h-4 mr-2" />
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                disabled={loading}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                <Save className="w-4 h-4 mr-2" />
                {loading ? 'Saving...' : 'Save Translations'}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default OfferTranslationModal;
